/**
 * Pattern Detection Radar Renderer
 * 
 * Renders radar/spider chart for multi-dimensional pattern analysis.
 * Shows multiple metrics on radial axes.
 */

import type { VisualizationRenderer } from '../registry.js';
import { designTokens, escapeHtml } from '../../../core/design-system.js';

export const renderPatternDetectionRadar: VisualizationRenderer = (data, meta) => {
  // Expected data format: array of { label: string, values: Record<string, number> }
  // Or: { categories: string[], series: Array<{ name: string, values: number[] }> }
  if (!data || (Array.isArray(data) && data.length === 0)) {
    return renderEmptyState('No data to display');
  }

  // Normalize data format
  let categories: string[] = [];
  let series: Array<{ name: string; values: number[] }> = [];

  if (Array.isArray(data)) {
    // Format: [{ label: string, values: { metric1: number, metric2: number, ... } }]
    const firstItem = data[0];
    if (firstItem && typeof firstItem === 'object' && firstItem.values) {
      categories = Object.keys(firstItem.values);
      series = data.map(item => ({
        name: item.label || item.name || 'Series',
        values: categories.map(cat => Number(item.values[cat]) || 0)
      }));
    } else {
      // Fallback: assume it's already in series format
      return renderEmptyState('Invalid data format for radar chart');
    }
  } else if (data.categories && data.series) {
    // Format: { categories: string[], series: Array<{ name: string, values: number[] }> }
    categories = data.categories;
    series = data.series;
  } else {
    return renderEmptyState('Invalid data format for radar chart');
  }

  if (categories.length === 0 || series.length === 0) {
    return renderEmptyState('No data to display');
  }

  // Chart dimensions
  const size = 500;
  const padding = 80;
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = (size - padding * 2) / 2;

  // Calculate angles for each category
  const angleStep = (2 * Math.PI) / categories.length;
  const categoryPoints = categories.map((cat, index) => {
    const angle = (index * angleStep) - (Math.PI / 2); // Start at top
    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);
    return { category: cat, angle, x, y };
  });

  // Find max value for scaling
  const maxValue = Math.max(
    ...series.flatMap(s => s.values),
    1 // Ensure at least 1 to avoid division by zero
  );

  // Generate grid circles (concentric rings)
  const gridLevels = 5;
  const gridCircles = Array.from({ length: gridLevels }, (_, i) => {
    const level = i + 1;
    const r = (radius / gridLevels) * level;
    const value = (maxValue / gridLevels) * level;
    
    return `
      <circle
        cx="${centerX}"
        cy="${centerY}"
        r="${r}"
        fill="none"
        stroke="${designTokens.borderColor}"
        stroke-width="0.5"
        opacity="0.3"
      />
      <text
        x="${centerX + r + 5}"
        y="${centerY}"
        font-size="${designTokens.fontSize.xs}"
        fill="${designTokens.contentColorMuted}"
        text-anchor="start"
      >${value.toFixed(1)}</text>
    `;
  }).join('');

  // Generate axis lines (from center to each category)
  const axisLines = categoryPoints.map(point => `
    <line
      x1="${centerX}"
      y1="${centerY}"
      x2="${point.x}"
      y2="${point.y}"
      stroke="${designTokens.borderColor}"
      stroke-width="1"
      opacity="0.5"
    />
  `).join('');

  // Generate category labels
  const categoryLabels = categoryPoints.map(point => {
    const labelX = centerX + (radius + 20) * Math.cos(point.angle);
    const labelY = centerY + (radius + 20) * Math.sin(point.angle);
    
    return `
      <text
        x="${labelX}"
        y="${labelY}"
        font-size="${designTokens.fontSize.sm}"
        fill="${designTokens.contentColor}"
        text-anchor="middle"
        font-weight="500"
      >${escapeHtml(point.category)}</text>
    `;
  }).join('');

  // Color palette for series
  const colors = [
    'rgba(102, 204, 255, 0.6)',
    'rgba(255, 200, 100, 0.6)',
    'rgba(100, 255, 150, 0.6)',
    'rgba(255, 100, 150, 0.6)',
    'rgba(200, 150, 255, 0.6)'
  ];

  // Generate radar polygons for each series
  const radarPolygons = series.map((serie, seriesIndex) => {
    const points = categoryPoints.map((point, catIndex) => {
      const value = serie.values[catIndex] || 0;
      const scaledRadius = (value / maxValue) * radius;
      const x = centerX + scaledRadius * Math.cos(point.angle);
      const y = centerY + scaledRadius * Math.sin(point.angle);
      return `${x},${y}`;
    }).join(' ');

    const color = colors[seriesIndex % colors.length];
    const strokeColor = color.replace('0.6', '0.9');

    return `
      <polygon
        points="${points}"
        fill="${color}"
        stroke="${strokeColor}"
        stroke-width="2"
        opacity="0.7"
        style="cursor: pointer; transition: opacity 0.2s;"
        data-series="${escapeHtml(serie.name)}"
        onmouseenter="this.style.opacity='1';"
        onmouseleave="this.style.opacity='0.7';"
      />
    `;
  }).join('');

  // Generate legend
  const legend = series.map((serie, index) => {
    const color = colors[index % colors.length];
    return `
      <g style="cursor: pointer;">
        <rect
          x="${size + 20}"
          y="${20 + index * 25}"
          width="15"
          height="15"
          fill="${color}"
          stroke="${color.replace('0.6', '0.9')}"
          stroke-width="1"
        />
        <text
          x="${size + 40}"
          y="${32 + index * 25}"
          font-size="${designTokens.fontSize.sm}"
          fill="${designTokens.contentColor}"
        >${escapeHtml(serie.name)}</text>
      </g>
    `;
  }).join('');

  const title = meta?.title || 'Pattern Detection Radar';

  return `
    <div style="width: 100%;">
      ${title ? `
        <div style="
          margin-bottom: ${designTokens.padding.md};
          font-size: ${designTokens.fontSize.md};
          font-weight: 600;
          color: ${designTokens.contentColor};
        ">${escapeHtml(title)}</div>
      ` : ''}
      <div style="
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
      ">
        <svg
          width="${size + 200}"
          height="${size}"
          style="
            display: block;
            max-width: 100%;
            height: auto;
          "
        >
          ${gridCircles}
          ${axisLines}
          ${categoryLabels}
          ${radarPolygons}
          ${legend}
        </svg>
      </div>
    </div>
  `;
};

function renderEmptyState(message: string): string {
  return `
    <div style="
      padding: ${designTokens.padding.xl};
      text-align: center;
      color: ${designTokens.contentColorMuted};
      font-size: ${designTokens.fontSize.sm};
    ">${escapeHtml(message)}</div>
  `;
}


